import { Injectable } from '@angular/core';

import { BankLinkingService } from '@app/shared/services/bank-linking/bank-linking.service';
import { SessionService } from '@app/shared/services';
import {
  LDCLPBPPublicApiModelsBanksBankAccount,
  LDCLPBPPublicApiModelsBanksBankLinkBeginResponse
} from '../../../../../generated/internalApi/model/models';
import {
  LDCLPBPPublicApiModelsLoanApplicationsAgentNoteCreateAgentNoteRequest,
} from '../../../../../generated/internalApi';

import { Subject } from 'rxjs';
import { AgentNoteService } from '@app/shared/services/agent-note/agent-note.service';
import { bodyScroll, toInteger } from '@app/utils/utils';
import { PersonalLoanConfigurationService } from '@app/pages/personal-loans/shared/services/personal-loan-configuration.service';

declare global {
  interface Window { finicityConnect: any; }
}

@Injectable()
export class FinicityService {

  private static readonly FINICITY_IFRAME_TIMEOUT_KEY = 'FINICITY_IFRAME_TIMEOUT_MS';
  private timeoutMs = 10000;
  public static readonly BANK_LINKING_CLOSING_MESSAGE: string =
    'Thank you! We are putting the finishing touches on linking your account. ' +
    'Please stand by, this could take up to a minute.';

  public loadingChanged = new Subject<boolean | string>();
  public onBankLinkingDone = new Subject<LDCLPBPPublicApiModelsBanksBankAccount>();
  public onError = new Subject<string>();

  constructor(
    private agentNoteService: AgentNoteService,
    private bankLinkingService: BankLinkingService,
    private sessionService: SessionService,
    private personalLoanConfigService: PersonalLoanConfigurationService
  ) {
    this.personalLoanConfigService.getAppProperty(FinicityService.FINICITY_IFRAME_TIMEOUT_KEY).subscribe(
      res => {
        this.timeoutMs = toInteger(res) || this.timeoutMs;
      },
      err => {
        console.error('Failed to read app property for Finicity iFrame timeout - using default.', err);
      }
    );
  }

  public openBankLinking(): void {
    this.loadingChanged.next(true); // spin while opening modal
    const errHandler = (error: string = null) => {
      this.createAgentNote(null, '', 'Failed');
      this.loadingChanged.next(false);
      this.onError.next();
      // @TODO Error message to user - improved bank linking service is unavailable
      console.error(error);
    };

    this.bankLinkingService.postBankLinkBegin().subscribe(
      data => {
        this.loadingChanged.next(false);
        if (data.urlSource) {
          let iframeResponded = false;
          const container = document.getElementById('finicity-iframe-container');
          container.style.display = 'block';
          bodyScroll(false);

          // Use Finicity helper to create iframe and overlay it on our page as a modal
          (window.finicityConnect.connectIFrame(data.urlSource, {
            overlay: 'rgba(0, 0, 0, 0.3)',
            selector: '#finicity-iframe-container',
            loaded: () => {
              iframeResponded = true;
              this.createAgentNote(data.bankingCustomerGuid, '', 'Opened');
            },
            success: (queryParams) => {
              container.style.display = 'none';
              bodyScroll(true);
              this.improvedLinkConfirm(data, queryParams);
            }
          }) as Promise<any>)
            .catch((err: Error) => {
              container.style.display = 'none';
              bodyScroll(true);
              if ((err as any).code.toLowerCase() === 'cancel') {
                this.improvedLinkConfirm(data, { success: false, error: err.message, reasonCode: (err as any).code });
              } else {
                errHandler(err.message);
              }
            });

          // Set timer to display timeout error msg if no response from iframe
          setTimeout(() => {
            if (!iframeResponded) {
              // create agent note to log iframe timeout
              this.createAgentNote(data.bankingCustomerGuid, '', 'Timeout');
              this.closeBankLinking();
              this.onError.next();
            }
          }, this.timeoutMs);
        } else {
          errHandler('No link to Finicity iFrame was provided.');
        }
      },
      err => {
        return errHandler(err);
      }
    );
  }

  public closeBankLinking(): void {
    this.loadingChanged.next(false);
    document.getElementById('finicity-iframe-container').style.display = 'none';
    bodyScroll(true);
    if (window.finicityConnect && window.finicityConnect.destroyIFrame) {
      window.finicityConnect.destroyIFrame();
    }
  }

  private improvedLinkConfirm(data: LDCLPBPPublicApiModelsBanksBankLinkBeginResponse, body: { [name: string]: boolean | string }): void {
    body.linkToken = data.linkToken;
    body.bankingCustomerGuid = data.bankingCustomerGuid;
    let newNoteStatusValue: string = null;

    if (body.success) {
      this.loadingChanged.next(FinicityService.BANK_LINKING_CLOSING_MESSAGE); // spin and show message while confirming data
      newNoteStatusValue = 'Finished';
      this.bankLinkingService.postBankLinkConfirm(body)
      .subscribe(
        res => {
          this.loadingChanged.next(false);
          if (body.success) {
            // if successful then done
            this.onBankLinkingDone.next(res.account);
          }
        },
        err => {
          console.error('error during account link confirmation', err);
          this.loadingChanged.next(false);
          const regMatch = err._body.match(/Code:\s*(\d+)/);
          let code: string = null;
          if (regMatch) {
            code = regMatch[1];
          }
          this.onError.next(code);
        }
      );
    } else {
      this.loadingChanged.next(false);
      newNoteStatusValue = 'Cancelled';
    }

    // create agent note to log iframe finished or cancelled
    this.createAgentNote(data.bankingCustomerGuid, 'Opened', newNoteStatusValue);
  }

  private createAgentNote(bankingCustomerGuid: string, oldValue: string, newValue: string): void {
    let entityType: string = null;
    let entityName: string = null;
    let changeType: string = null;
    let changeTypeEnum: string = null;
    if (bankingCustomerGuid) {
      entityType = 'Partner_BankingCustomer';
      entityName = 'Finicity';
      changeType = 'iFrame';
      changeTypeEnum = 'partner.BankingCustomer.iFrame';
    } else {
      changeType = 'Instant Bank Link';
      changeTypeEnum = 'partner.BankingCustomer';
    }

    const request: LDCLPBPPublicApiModelsLoanApplicationsAgentNoteCreateAgentNoteRequest = {
      category: 'BankLinking',
      isSystemGenerated: true,
      message: '',
      entityType: entityType,
      entityTypeGuid: bankingCustomerGuid,
      entityName: entityName,
      changeDetailJSON: [{
        changeType: changeType,
        oldValue: oldValue,
        newValue: newValue,
        changeTypeEnum: changeTypeEnum,
        oldEnum: oldValue,
        newEnum: `${changeTypeEnum}.${newValue}`
      }]
    };

    this.agentNoteService
      .createAgentNote(this.sessionService.personalLoanSession.data.loanApplicationGuid, request)
      .subscribe();
  }
}
